/**
 * PATH: packages/job-hub/src/jobs/qa/qaAreaMenu.ts
 * FILE_ID: JOBHUB-QA-AREA-MENU-001
 * PURPOSE: Right-click menu for one area in the QA areas rail.
 */
import { ArrowDown, ArrowUp, ClipboardPaste, Copy, FolderInput, Pencil, Plus, Trash2 } from 'lucide-react'
import type { ContextMenuItem } from '@quotech/ui'

export function buildQaAreaMenu({
  pasteAreaLabel,
  pasteChecklistLabel,
  onCopy,
  onPasteAreaAbove,
  onPasteAreaBelow,
  onPasteChecklist,
  onRename,
  onDuplicate,
  onInsertBlank,
  onDelete,
  onContainedIn,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  copyLabel = 'Copy area',
  pasteAboveLabel = 'Paste area above',
  pasteBelowLabel = 'Paste area below',
  pasteIntoLabel = 'Paste checklist into this area',
  emptyAreaHint = 'Copy an area first',
  emptyIntoHint = 'Copy a checklist first',
  insertLabel = 'Insert blank area',
}: {
  pasteAreaLabel: string | null
  pasteChecklistLabel: string | null
  onCopy: () => void
  onPasteAreaAbove: () => void
  onPasteAreaBelow: () => void
  onPasteChecklist: () => void
  onRename: () => void
  onDuplicate?: () => void
  onInsertBlank?: () => void
  onDelete?: () => void
  onContainedIn?: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  canMoveUp?: boolean
  canMoveDown?: boolean
  copyLabel?: string
  pasteAboveLabel?: string
  pasteBelowLabel?: string
  pasteIntoLabel?: string
  emptyAreaHint?: string
  emptyIntoHint?: string
  insertLabel?: string
}): ContextMenuItem[] {
  // Reused from: packages/job-hub/src/jobs/qa/qaChecklistRowMenu.ts (ContextMenuItem builder)
  return [
    { id: 'copy-area', label: copyLabel, icon: Copy, onSelect: onCopy },
    {
      id: 'paste-area-above',
      label: pasteAboveLabel,
      icon: ClipboardPaste,
      hint: pasteAreaLabel ?? emptyAreaHint,
      disabled: !pasteAreaLabel,
      onSelect: onPasteAreaAbove,
    },
    {
      id: 'paste-area-below',
      label: pasteBelowLabel,
      icon: ClipboardPaste,
      hint: pasteAreaLabel ?? emptyAreaHint,
      disabled: !pasteAreaLabel,
      onSelect: onPasteAreaBelow,
    },
    {
      id: 'paste-checklist',
      label: pasteIntoLabel,
      icon: ClipboardPaste,
      hint: pasteChecklistLabel ?? emptyIntoHint,
      disabled: !pasteChecklistLabel,
      onSelect: onPasteChecklist,
    },
    { id: 'sep-rename', kind: 'separator' },
    { id: 'rename', label: 'Rename', icon: Pencil, onSelect: onRename },
    ...(onMoveUp ? [{ id: 'move-up', label: 'Move up', icon: ArrowUp, disabled: canMoveUp === false, onSelect: onMoveUp }] : []),
    ...(onMoveDown ? [{ id: 'move-down', label: 'Move down', icon: ArrowDown, disabled: canMoveDown === false, onSelect: onMoveDown }] : []),
    ...(onDuplicate ? [{ id: 'duplicate-area', label: 'Duplicate', icon: Copy, onSelect: onDuplicate }] : []),
    ...(onInsertBlank ? [{ id: 'insert-blank-area', label: insertLabel, icon: Plus, onSelect: onInsertBlank }] : []),
    ...(onContainedIn ? [{ id: 'contained-in', label: 'Belongs to Level:', icon: FolderInput, onSelect: onContainedIn }] : []),
    ...(onDelete ? [{ id: 'delete-area', kind: 'destructive' as const, label: 'Delete', icon: Trash2, onSelect: onDelete }] : []),
  ]
}
